import { store, mutations } from '@/store';
import { applySensitiveContentBlock } from '@/utils/sensitiveContent';
import { WEBSCRAPE_API_URL } from '@/api/config';
import { streamChat } from '@/api/chat';

/**
 * 智能搜索与网页抓取问答模块 (Frontend-driven Agentic Flow)
 */
export class SmartSearchQA {
  /**
   * 处理后端下发的 web_search decision，执行抓取并再次流式请求 LLM 总结
   */
  static async handleWebSearchDecision(vm, userQuery, urls, aiMsgIndex) {
    const currentMsg = store.history[aiMsgIndex];
    if (!currentMsg) return;

    try {
      vm.isSending = true; // 锁定输入框，避免打断
      vm.abortController = new AbortController();

      // 仅抓取前三个链接，确保性能和响应质量
      const targetUrls = urls.slice(0, 3);

      // 1. 开始抓取
      currentMsg.chatStatus = 'scraping';
      vm.scrapeTimeElapsed = 0;
      let finishedCount = 0;
      
      const updateStatus = () => {
         if (currentMsg && currentMsg.chatStatus === 'scraping') {
            const total = targetUrls.length;
            if (total === 1) {
               currentMsg.statusText = `正在获取网页内容 ${targetUrls[0]} (${vm.scrapeTimeElapsed.toFixed(1)}s)...`;
            } else {
               currentMsg.statusText = `正在获取网页内容 (${finishedCount}/${total}) (${vm.scrapeTimeElapsed.toFixed(1)}s)...`;
            }
         }
      };

      updateStatus();
      vm.scrapeTimer = setInterval(() => {
         vm.scrapeTimeElapsed += 0.1;
         updateStatus();
      }, 100);

      if (vm.scrollToBottom) vm.scrollToBottom(true);

      const scrapePromises = targetUrls.map(async (url) => {
        try {
          const myHeaders = new Headers();
          myHeaders.append("user-agent", "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/138.0.0.0 Safari/537.36 Edg/138.0.0.0");
          myHeaders.append("Content-Type", "application/json");

          const raw = JSON.stringify({
            "url": url,
            "engine": "cheerio",
            "wait_for": 200,
            "formats": ["markdown"]
          });

          const requestOptions = {
            method: 'POST',
            headers: myHeaders,
            body: raw,
            redirect: 'follow',
            signal: vm.abortController?.signal
          };

          const response = await fetch(WEBSCRAPE_API_URL, requestOptions);
          const result = await response.json();
          
          if (result.success && result.data && result.data.markdown) {
            return {
                url: url,
                title: result.data.title || url,
                markdown: result.data.markdown
            };
          }
          return null;
        } catch (error) {
          if (error.name === 'AbortError') {
             console.log('Scrape aborted by user');
             return { aborted: true };
          }
          console.error(`抓取失败: ${url}`, error);
          return null; 
        } finally {
          finishedCount++;
          updateStatus();
        }
      });

      const results = await Promise.allSettled(scrapePromises);
      
      if (vm.scrapeTimer) {
        clearInterval(vm.scrapeTimer);
        vm.scrapeTimer = null;
      }
      
      const validResults = results
        .filter(r => r.status === 'fulfilled' && r.value && !r.value.aborted)
        .map(r => r.value);
        
      const isAborted = results.some(r => r.status === 'fulfilled' && r.value && r.value.aborted);

      // 4. 发起流式总结请求
      let systemContent = '';

      if (validResults.length === 0) {
        currentMsg.chatStatus = 'summarizing';
        if (isAborted) {
           currentMsg.statusText = '网页抓取已取消，正在总结...';
        } else {
           currentMsg.statusText = '网页抓取失败，正在总结...';
        }
      } else {
        // 2. 组装上下文（不再作为显式附件展示在 UI）
        let combinedMarkdown = validResults.map(r => `来源: ${r.url}\n\n${r.markdown}`).join('\n\n---\n\n');
        
        // 限制总长度，防止 Payload 过大
        if (combinedMarkdown.length > 15000) {
           combinedMarkdown = combinedMarkdown.substring(0, 15000) + '\n\n[由于长度限制，后续内容已截断]';
        }

        // 3. 状态：总结与输出
        currentMsg.chatStatus = 'summarizing';
        currentMsg.statusText = `已深度阅读 ${validResults.length} 篇参考资料，正在总结...`;
        
        systemContent = `以下是系统为你抓取到的最新参考资料（Markdown格式）：\n\n${combinedMarkdown}\n\n请基于上述资料，准确、详实地回答用户的问题。如果资料中没有相关信息，请明确告知，不要编造。不要说明这些资料是如何获得的。\n\n你已经拥有回答所需的网页内容，不要再调用搜索或网页抓取工具，直接回答。`;
      }
      
      if (vm.scrollToBottom) vm.scrollToBottom(true);
      
      let context = store.history.slice(0, aiMsgIndex);
      if (systemContent) {
        context.unshift({ role: 'system', content: systemContent });
      }
console.log("streamChat",1);
      // 获取用户原有附件（如果有）
      const userMsg = store.history[aiMsgIndex - 1];
      const existingAttachments = userMsg ? (userMsg.attachments || []) : [];
      const disableFollowUpTools = ['perform_web_search', 'web_search', 'search', 'fetch_webpage_content'];
console.log("streamChat",userQuery, context);
      await streamChat(userQuery, context, {
        onContent: (chunk) => {
            currentMsg.chatStatus = 'streaming';
            currentMsg.statusText = '';
            currentMsg.content += chunk;
            if (vm.scrollToBottom) vm.scrollToBottom();
        },
        onThinking: (chunk) => {
            currentMsg.thinking += chunk;
            currentMsg.isThinking = true;
            if (vm.scrollToBottom) vm.scrollToBottom();
        },
        onSensitiveContent: () => {
            applySensitiveContentBlock(currentMsg);
            vm.isSending = false;
            vm.abortController = null;
            if (vm.scrollToBottom) vm.scrollToBottom(true);
            mutations.finalizeLastMessage();
        },
        onDone: () => {
            vm.isSending = false;
            vm.abortController = null;
            currentMsg.chatStatus = 'idle';
            currentMsg.isThinking = false;
            try {
              mutations.finalizeLastMessage();
            } catch (e) {}
        },
        onError: (err) => {
            console.error(err);
            vm.isSending = false;
            vm.abortController = null;
            currentMsg.chatStatus = 'idle';
            currentMsg.content += '\n\n[总结生成失败]';
        },
        signal: vm.abortController?.signal
      }, existingAttachments, { disable_tools: disableFollowUpTools });
      
    } catch (err) {
      if (err.name !== 'AbortError') {
         console.error('SmartSearchQA Error:', err);
      }
      currentMsg.chatStatus = 'idle';
      currentMsg.statusText = '';
      vm.isSending = false;
      vm.abortController = null;
    }
  }
}
